import { PMS_BOARD_LABELS, pmsNightCount, type PmsBoard } from '@lemontaigu/pms-core';
import type { ChargeDay, RoomStayView } from '@lemontaigu/pms-core';

// What the rack inspector says about money, from the booking's CHARGE LINES —
// the same evidence the reservation pop-up reads. Not from `totalCents`: that
// is the whole booking, so dividing it by one stay's nights (what the pane did
// until 2026-10-19) read « 3 598 € / nuit » on a 25-room group at 135 €.

export interface RackStayMoney {
  /** This room's board — « DP », « Pdj », « Ch »; null when the plan is unknown. */
  board: PmsBoard | null;
  /** The room's night price: one figure when every night is the same, else
   *  the range. Includes what the board includes (DP is in the rate). */
  nightCents: { min: number; max: number } | null;
  /** The units every room line of this room bills (« 2 » for a group priced per
   *  person, « 5 » for five rooms Vega left on it) — 1 for one room, null when its
   *  lines differ. Read with `roomLineMark` (roomLineView). */
  nightQuantity: number | null;
  /** This room: nights (room lines), extras, taxe de séjour, and their sum. */
  roomCents: number;
  extrasCents: number;
  taxCents: number;
  totalCents: number;
  /** The whole booking, room lines only, per room-night — what the pop-up's
   *  « / nuit moy. » shows. Null on a one-room booking (it would repeat). */
  bookingAvgCents: number | null;
  bookingTaxCents: number;
  bookingTaxNights: number;
  /** Board mix over the booking's live rooms — « 25 DP ». */
  boardMix: { board: PmsBoard; count: number }[];
}

export function rackStayMoney(
  stay: RoomStayView,
  siblings: readonly RoomStayView[],
  lines: readonly ChargeDay[],
  boardOf: (ratePlanId: number) => PmsBoard | null,
): RackStayMoney {
  const live = lines.filter((l) => l.voidedAt === null);
  const mine = live.filter((l) => l.roomStayId === stay.id);
  const sum = (xs: readonly ChargeDay[]) => xs.reduce((s, l) => s + l.amountCents, 0);

  const nights = mine.filter((l) => l.kind === 'room');
  const unit = nights.map((l) => l.unitCents);
  const roomCents = sum(nights);
  const extrasCents = sum(mine.filter((l) => l.kind === 'product'));
  const taxCents = sum(mine.filter((l) => l.kind === 'tax'));

  const all = [stay, ...siblings];
  const roomNights = all.reduce((n, s) => n + Math.max(pmsNightCount(s.checkinDate, s.checkoutDate), 0), 0);
  const bookingRoom = sum(live.filter((l) => l.kind === 'room'));
  const tax = live.filter((l) => l.kind === 'tax');

  const counts = new Map<PmsBoard, number>();
  for (const s of all) {
    const b = boardOf(s.ratePlanId);
    if (b !== null) counts.set(b, (counts.get(b) ?? 0) + 1);
  }

  return {
    board: boardOf(stay.ratePlanId),
    nightCents: unit.length === 0 ? null : { min: Math.min(...unit), max: Math.max(...unit) },
    nightQuantity: new Set(nights.map((l) => l.quantity)).size === 1 ? nights[0]!.quantity : nights.length === 0 ? 1 : null,
    roomCents,
    extrasCents,
    taxCents,
    totalCents: roomCents + extrasCents + taxCents,
    bookingAvgCents: siblings.length === 0 || roomNights === 0 ? null : Math.round(bookingRoom / roomNights),
    bookingTaxCents: sum(tax),
    bookingTaxNights: tax.reduce((n, l) => n + (l.personsTaxable ?? 0), 0),
    boardMix: (['hb', 'bb', 'none'] as PmsBoard[])
      .map((board) => ({ board, count: counts.get(board) ?? 0 }))
      .filter((m) => m.count > 0),
  };
}

/** « DP — demi-pension » : the short code the rack and the pop-up use, spelled out. */
export const BOARD_LONG_FR: Record<PmsBoard, string> = {
  hb: `${PMS_BOARD_LABELS.hb} — demi-pension`,
  bb: `${PMS_BOARD_LABELS.bb} — petit-déjeuner`,
  none: `${PMS_BOARD_LABELS.none} — chambre seule`,
};
